import { lazy, Suspense } from "react";
import OrderCard from "./OrderCard.jsx";
import { DISPUTES_ENABLED } from "../lib/features.js";
import { orderStatusLabel } from "../lib/orderPresentation.js";

const OrderParticipantContact = lazy(
  () => import("./OrderParticipantContact.jsx")
);

export default function OrdersSection({
  role,
  userId,
  orders,
  filteredOrders,
  search,
  onSearchChange,
  status,
  onStatusChange,
  hasFilter,
  onClearFilters,
  disputes,
  disputesLoaded,
  disputesError,
  onRetryDisputes,
  buyerProfiles,
  buyerProfilesLoading,
  buyerProfilesError,
  onRetryBuyerProfiles,
  travelerProfiles,
  travelerProfilesLoading,
  travelerProfilesError,
  onRetryTravelerProfiles,
  loading,
  loaded,
  error,
  onRetryOrders,
  hasMore,
  moreLoading,
  moreError,
  onLoadMore,
  statusLabels,
  canReviewParticipant,
  onOpenReviews,
  onAuthorize,
  onCancel,
  onDecline,
  onAdvance,
  onReportIssue,
  onOpenChat,
}) {
  const ordersVariant = "a";

  return (
    <>
      <h1>Orders</h1>
      <section
        className="marketplace-filters"
        aria-label="Search and filter orders"
      >
        <label htmlFor="order-search">
          Search by item or delivery area
          <input
            id="order-search"
            type="search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="e.g. coffee, Paranaque"
          />
        </label>
        <label htmlFor="order-status">
          Filter by status
          <select
            id="order-status"
            value={status}
            onChange={(event) => onStatusChange(event.target.value)}
          >
            <option value="">All statuses</option>
            {[
              "requested",
              "accepted",
              "purchased",
              "in_transit",
              "delivered",
              "cancelled",
              "disputed",
            ].map((orderStatus) => (
              <option key={orderStatus} value={orderStatus}>
                {orderStatusLabel(orderStatus)}
              </option>
            ))}
          </select>
        </label>
        {hasFilter && (
          <button type="button" onClick={onClearFilters}>
            Clear filters
          </button>
        )}
      </section>
      {disputesError && (
        <p className="marketplace-load-error" role="alert">
          {disputesError}{" "}
          <button type="button" onClick={onRetryDisputes}>
            Retry
          </button>
        </p>
      )}
      {role === "traveler" && buyerProfilesError && (
        <p className="marketplace-load-error" role="alert">
          Could not load buyer profiles. {buyerProfilesError}{" "}
          <button type="button" onClick={onRetryBuyerProfiles}>
            Retry
          </button>
        </p>
      )}
      {role === "buyer" && travelerProfilesError && (
        <p className="marketplace-load-error" role="alert">
          Could not load traveler profiles. {travelerProfilesError}{" "}
          <button type="button" onClick={onRetryTravelerProfiles}>
            Retry
          </button>
        </p>
      )}
      {loading || !loaded ? (
        <p className="marketplace-empty" role="status" aria-live="polite">
          Loading orders…
        </p>
      ) : error ? (
        <p className="marketplace-load-error" role="alert">
          Could not load orders. {error}{" "}
          <button type="button" onClick={onRetryOrders}>
            Retry
          </button>
        </p>
      ) : (
        <>
          <p className="marketplace-results" role="status" aria-live="polite">
            Showing {filteredOrders.length} matching orders among{" "}
            {orders.length} loaded.
            {hasMore && hasFilter
              ? " Load more to search additional orders."
              : ""}
          </p>
          {orders.length === 0 ? (
            <p className="marketplace-empty" role="status">
              You don’t have any orders yet.
            </p>
          ) : filteredOrders.length === 0 ? (
            <p className="marketplace-empty" role="status">
              No loaded orders match these filters.
            </p>
          ) : (
            <div className={`orders orders--variant-${ordersVariant}`}>
              {filteredOrders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  role={role}
                  userId={userId}
                  variant={ordersVariant}
                  buyerProfile={buyerProfiles[order.buyer_id]}
                  buyerProfilesLoading={buyerProfilesLoading}
                  travelerProfile={travelerProfiles[order.traveler_id]}
                  travelerProfilesLoading={travelerProfilesLoading}
                  contactTools={
                    [
                      "accepted",
                      "purchased",
                      "in_transit",
                      "delivered",
                      "disputed",
                    ].includes(order.status) && (
                      <Suspense
                        fallback={
                          <p role="status">Loading contact tools…</p>
                        }
                      >
                        <OrderParticipantContact orderId={order.id} />
                      </Suspense>
                    )
                  }
                  canReviewParticipant={canReviewParticipant}
                  onOpenReviews={() =>
                    onOpenReviews(
                      role === "buyer" ? order.traveler_id : order.buyer_id
                    )
                  }
                  dispute={disputes[order.id]}
                  statusLabels={statusLabels}
                  canReportIssue={
                    DISPUTES_ENABLED &&
                    disputesLoaded &&
                    statusLabels.includes(order.status) &&
                    !disputes[order.id]
                  }
                  onAuthorize={() => onAuthorize(order)}
                  onCancel={() => onCancel(order)}
                  onDecline={() => onDecline(order)}
                  onAdvance={() => onAdvance(order)}
                  onReportIssue={() => onReportIssue(order)}
                  onOpenChat={() => onOpenChat(order)}
                />
              ))}
            </div>
          )}
        </>
      )}
      {moreError && (
        <p className="marketplace-load-error" role="alert">
          Could not load more orders. {moreError}{" "}
          <button type="button" onClick={onLoadMore}>
            Retry
          </button>
        </p>
      )}
      {hasMore && (
        <button
          className="marketplace-load-more"
          type="button"
          disabled={moreLoading || loading}
          onClick={onLoadMore}
        >
          {moreLoading ? "Loading more orders…" : "Load more orders"}
        </button>
      )}
    </>
  );
}
